import React, { useState, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import { useMFA } from '../../contexts/MFAContext';
import { MFATypes } from '@flashbacktech/flashbackclient/api';
import {
  base64UrlToArrayBuffer,
  serializeRegistrationCredential,
  stringToArrayBuffer,
} from '../../utils/passkey';

// WebAuthn types
declare global {
  interface Window {
    PublicKeyCredential: typeof PublicKeyCredential;
  }
}

interface PasskeySetupProps {
  onSetupComplete: () => void;
}

interface PasskeyChallengeData {
  challenge: string;
  rpName?: string;
  userName?: string;
  rpID?: string;
  userId?: string;
}

const PasskeySetup: React.FC<PasskeySetupProps> = ({ onSetupComplete }) => {
  const { mfaState, setupMFAMethod, verifyMFASetup, clearError } = useMFA();
  const [isCreating, setIsCreating] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState<string>('');
  const [isInitializing, setIsInitializing] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);

  // Check if WebAuthn is supported
  const isWebAuthnSupported = () => {
    return (
      window.PublicKeyCredential &&
      typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function'
    );
  };

  const getDeviceInfo = () => {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;

    if (userAgent.includes('Chrome')) return 'Chrome Browser';
    if (userAgent.includes('Firefox')) return 'Firefox Browser';
    if (userAgent.includes('Safari')) return 'Safari Browser';
    if (userAgent.includes('Edge')) return 'Edge Browser';

    if (platform.includes('Mac')) return 'macOS Device';
    if (platform.includes('Win')) return 'Windows Device';
    if (platform.includes('Linux')) return 'Linux Device';

    return 'Unknown Device';
  };

  const handleSetupClick = () => {
    if (!isWebAuthnSupported()) return;
    setShowConfirm(true);
  };

  const handleSetupConfirmed = async () => {
    setShowConfirm(false);
    if (!isWebAuthnSupported()) return;

    clearError();
    setIsCreating(true);

    try {
      // Setup passkey and capture the response directly
      const setupResponse = await setupMFAMethod(MFATypes.MFAType.PASSKEY);

      // Extract challenge data from the response
      let challengeData: PasskeyChallengeData | null = null;
      if (setupResponse?.data) {
        const data = setupResponse.data as Partial<MFATypes.PasskeySetupData>;
        if (typeof data.challenge !== 'string') {
          throw new Error('Invalid passkey challenge received from backend');
        }
        challengeData = {
          challenge: data.challenge,
          rpName: typeof data.rpName === 'string' ? data.rpName : undefined,
          userName: typeof data.userName === 'string' ? data.userName : undefined,
          rpID: typeof data.rpID === 'string' ? data.rpID : undefined,
          userId: typeof data.userId === 'string' ? data.userId : undefined,
        };
      } else {
        challengeData = mfaState.setupData
          ? {
              challenge: mfaState.setupData.challenge,
              rpName: mfaState.setupData.rpName,
              userName: mfaState.setupData.userName,
              rpID: mfaState.setupData.rpID,
              userId: mfaState.setupData.userId,
            }
          : null;
      }

      if (challengeData?.challenge) {
        // Create the publicKey options for native WebAuthn API
        const publicKeyOptions: PublicKeyCredentialCreationOptions = {
          challenge: base64UrlToArrayBuffer(challengeData.challenge),
          rp: {
            id: challengeData.rpID || process.env.REACT_APP_RP_ID || window.location.hostname,
            name: challengeData.rpName || 'Flashback Dashboard',
          },
          user: {
            id: stringToArrayBuffer(challengeData.userId || challengeData.userName || 'user'),
            name: challengeData.userName || 'user@example.com',
            displayName: challengeData.userName || 'User',
          },
          pubKeyCredParams: [
            {
              type: 'public-key' as const,
              alg: -7, // ES256
            },
            {
              type: 'public-key' as const,
              alg: -257, // RS256
            },
          ],
          authenticatorSelection: {
            authenticatorAttachment: 'platform',
            userVerification: 'required',
            residentKey: 'preferred',
            requireResidentKey: false,
          },
          timeout: 60000,
          attestation: 'none',
        };

        // Create credentials using native WebAuthn API
        const credential = await navigator.credentials.create({
          publicKey: publicKeyOptions,
        });

        if (credential) {
          const convertedCredential = serializeRegistrationCredential(credential as PublicKeyCredential);
          const success = await verifyMFASetup(MFATypes.MFAType.PASSKEY, undefined, {
            ...convertedCredential,
            challenge: challengeData.challenge,
          });

          if (!success) {
            return;
          }

          setIsComplete(true);
          onSetupComplete();
        } else {
          throw new Error('No credential received from WebAuthn');
        }
      } else {
        throw new Error('No challenge data received from backend');
      }
    } catch (error) {
      // Errors are surfaced by MFA context state
    } finally {
      setIsCreating(false);
    }
  };

  const handleRetry = () => {
    setIsComplete(false);
    setDeviceInfo(getDeviceInfo());
  };

  const error = mfaState.error;
  const isSettingUp = mfaState.isSettingUp;

  // Automatically initialize setup when component mounts
  useEffect(() => {
    setDeviceInfo(getDeviceInfo());

    // Small delay to show loading state, then proceed to setup screen
    const timer = setTimeout(() => {
      setIsInitializing(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Card wrapper style
  const cardStyle: React.CSSProperties = {
    background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)', overflow: 'hidden',
  };

  if (isInitializing) {
    return (
      <div style={cardStyle}>
        <div style={{ padding: '40px 32px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
          <CircularProgress size={22} sx={{ color: '#617BFF' }} />
          <span style={{ fontSize: '14px', color: '#535862' }}>Initializing Passkey setup...</span>
        </div>
      </div>
    );
  }

  if (isComplete) {
    return (
      <div style={cardStyle}>
        <div style={{ padding: '32px', textAlign: 'center' }}>
          {/* Success icon */}
          <div style={{ width: 56, height: 56, borderRadius: '14px', background: '#ECFDF3', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#079455" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#181D27', margin: '0 0 8px', letterSpacing: '-0.01em' }}>
            Passkey Setup Complete!
          </h3>
          <p style={{ fontSize: '14px', color: '#717680', margin: '0 0 8px', lineHeight: 1.5 }}>
            Your passkey has been successfully configured for <strong style={{ color: '#344054' }}>{deviceInfo}</strong>
          </p>
          <p style={{ fontSize: '13px', color: '#98A2B3', margin: '0 0 28px', lineHeight: 1.5 }}>
            You can now use biometric authentication or device PIN to verify your identity.
          </p>

          <button
            onClick={handleRetry}
            style={{
              padding: '10px 24px', fontSize: '14px', fontWeight: 600, color: '#617BFF',
              background: '#F0EDFF', border: '1px solid #D9D2FF', borderRadius: '10px',
              cursor: 'pointer', transition: 'all 0.2s',
            }}
          >
            Setup Another Passkey
          </button>
        </div>
      </div>
    );
  }

  if (!isWebAuthnSupported()) {
    return (
      <div style={cardStyle}>
        <div style={{ padding: '32px', textAlign: 'center' }}>
          <div style={{ margin: '0 0 16px', padding: '12px 16px', borderRadius: '10px', background: '#FFFAEB', border: '1px solid #FEDF89', display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#DC6803" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#B54708' }}>WebAuthn is not supported in your browser</span>
          </div>
          <p style={{ fontSize: '14px', color: '#717680', margin: 0, lineHeight: 1.5 }}>
            Passkeys require a modern browser that supports WebAuthn. Please use a different MFA method or update your browser.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div style={{ padding: '32px 32px 24px', textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, borderRadius: '14px', background: '#FFF4ED', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#E04F16" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
          </svg>
        </div>
        <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#181D27', margin: '0 0 6px', letterSpacing: '-0.01em' }}>
          Passkey Setup
        </h3>
        <p style={{ fontSize: '14px', color: '#717680', margin: 0, lineHeight: 1.5 }}>
          Use biometric authentication or device PIN for secure access
        </p>
      </div>

      {/* Error */}
      {error && (
        <div style={{ margin: '0 32px 16px', padding: '12px 16px', borderRadius: '10px', background: '#FEF3F2', border: '1px solid #FECDCA', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D92D20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
          <span style={{ fontSize: '13px', color: '#B42318', flex: 1 }}>{error}</span>
          <button onClick={clearError} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D92D20', padding: 0, fontSize: '18px', lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Device info */}
      <div style={{ margin: '0 32px 20px', padding: '16px', borderRadius: '10px', background: '#F9FAFB', border: '1px solid #EAECF0' }}>
        <p style={{ fontSize: '13px', fontWeight: 600, color: '#344054', margin: '0 0 10px' }}>Device Information</p>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, color: '#617BFF', background: '#F0EDFF', border: '1px solid #D9D2FF', borderRadius: '20px' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>
            {deviceInfo}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, color: '#079455', background: '#ECFDF3', border: '1px solid #ABEFC6', borderRadius: '20px' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
            WebAuthn Supported
          </span>
        </div>
        <p style={{ fontSize: '13px', color: '#535862', margin: 0, lineHeight: 1.5 }}>
          This passkey will be created for your current device and browser. You can create additional passkeys for other devices.
        </p>
      </div>

      {/* Create button */}
      <div style={{ padding: '0 32px 24px' }}>
        <button
          onClick={handleSetupClick}
          disabled={isSettingUp || isCreating}
          style={{
            width: '100%', padding: '12px', fontSize: '14px', fontWeight: 600, color: '#FFFFFF',
            background: (isSettingUp || isCreating) ? '#B0BCFF' : '#617BFF',
            border: 'none', borderRadius: '10px', cursor: (isSettingUp || isCreating) ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          }}
          onMouseEnter={(e) => { if (!isSettingUp && !isCreating) (e.target as HTMLElement).style.background = '#5A6FE8'; }}
          onMouseLeave={(e) => { if (!isSettingUp && !isCreating) (e.target as HTMLElement).style.background = '#617BFF'; }}
        >
          {(isSettingUp || isCreating) ? <CircularProgress size={18} sx={{ color: '#FFFFFF' }} /> : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" /></svg>
          )}
          {(isSettingUp || isCreating) ? 'Creating Passkey...' : 'Create Passkey'}
        </button>
      </div>

      {/* How it works */}
      <div style={{ margin: '0 32px 24px', padding: '14px 16px', borderRadius: '10px', background: '#F9FAFB', border: '1px solid #EAECF0' }}>
        <p style={{ fontSize: '12px', color: '#535862', margin: 0, lineHeight: 1.6 }}>
          <strong style={{ color: '#344054' }}>How it works:</strong> Passkeys use your device's built-in security features like fingerprint, face recognition, or PIN. They're more secure than passwords and work across all your devices. The passkey is stored securely on your device and never shared with our servers.
        </p>
      </div>

      {/* Setup in progress prompt */}
      {isSettingUp && (
        <div style={{ margin: '0 32px 32px', padding: '14px 16px', borderRadius: '10px', background: '#EFF8FF', border: '1px solid #B2DDFF', textAlign: 'center' }}>
          <p style={{ fontSize: '13px', fontWeight: 500, color: '#1570EF', margin: 0 }}>
            Follow your device's security prompt to complete the setup...
          </p>
        </div>
      )}

      {/* Confirmation overlay */}
      {showConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setShowConfirm(false)}>
          <div style={{ background: '#FFFFFF', borderRadius: '16px', width: 400, maxWidth: '90vw', padding: '28px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ width: 48, height: 48, borderRadius: '12px', background: 'linear-gradient(135deg, #E04F16 0%, #F97316 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" /></svg>
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#181D27', margin: '0 0 4px' }}>Enable Passkey?</h3>
            <p style={{ fontSize: '13px', color: '#717680', lineHeight: 1.5, margin: '0 0 24px' }}>
              This will activate Passkey as a two-factor authentication method on your account. Your device will prompt you to use biometric authentication or PIN to create the passkey.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowConfirm(false)}
                style={{ padding: '10px 24px', fontSize: '13px', fontWeight: 600, color: '#535862', background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSetupConfirmed}
                style={{ padding: '10px 24px', fontSize: '13px', fontWeight: 600, color: '#FFFFFF', background: '#617BFF', border: 'none', borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseEnter={(e) => { (e.target as HTMLElement).style.background = '#5A6FE8'; }}
                onMouseLeave={(e) => { (e.target as HTMLElement).style.background = '#617BFF'; }}
              >
                Confirm & Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PasskeySetup;
